import { useState, useEffect } from "react";
import {
  RadioGroup,
  RadioGroupItem,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@fm/ui";
import type { VehicleDeliveryLocation } from "@fm/api/vehicle-delivery-locations";
import GooglePlacesAutocomplete from "./GooglePlacesAutocomplete";
import GoogleMap from "./GoogleMap";

export interface CustomAddress {
  address: string;
  city: string;
  state: string;
  zipCode?: string;
  country: string;
  lat: number;
  lng: number;
}

export interface DeliveryOptionsData {
  deliveryType: "pickup" | "delivery";
  deliveryOption?: "custom" | "location";
  selectedLocationId?: string;
  customAddress?: CustomAddress;
  deliveryDistance?: number | null;
}

export interface DeliveryOptionsSelectorProps {
  deliveryType: "pickup" | "delivery";
  onDeliveryTypeChange: (type: "pickup" | "delivery") => void;
  deliveryOption?: "custom" | "location";
  onDeliveryOptionChange: (option: "custom" | "location") => void;
  selectedLocationId?: string;
  onLocationSelect: (locationId: string) => void;
  customAddress?: CustomAddress | null;
  onCustomAddressChange: (address: CustomAddress | null) => void;
  deliveryLocations: VehicleDeliveryLocation[];
  deliveryFee: number;
  deliveryDistance: number | null;
  isCalculatingDistance?: boolean;
  deliveryAvailable?: boolean;
  /**
   * Whether the vehicle also accepts a typed address. When false the renter
   * can only pick one of the assigned delivery locations (no address field,
   * no autocomplete, no distance). Defaults to true (previous behavior).
   */
  customAddressAvailable?: boolean;
  vehicleAddress?: string;
  onAddressSelected?: (selected: boolean) => void;
  googleMapsApiKey?: string;
  className?: string;
}

/**
 * Component for selecting delivery options
 *
 * Features:
 * - Radio selection: Pickup vs Delivery
 * - For Delivery:
 *   - Radio: Custom Address vs Predefined Location
 *   - Google Places Autocomplete (requires external component)
 *   - Select from predefined locations
 * - Shows calculated distance and delivery fee
 *
 * Note: This component provides the structure and logic.
 * Google Places Autocomplete should be integrated separately.
 *
 * @example
 * ```tsx
 * <DeliveryOptionsSelector
 *   deliveryType={deliveryType}
 *   onDeliveryTypeChange={setDeliveryType}
 *   deliveryOption={deliveryOption}
 *   onDeliveryOptionChange={setDeliveryOption}
 *   selectedLocationId={selectedLocationId}
 *   onLocationSelect={setSelectedLocationId}
 *   customAddress={customAddress}
 *   onCustomAddressChange={setCustomAddress}
 *   deliveryLocations={locations}
 *   deliveryFee={deliveryFee}
 *   deliveryDistance={distance}
 * />
 * ```
 */
export function DeliveryOptionsSelector({
  deliveryType,
  onDeliveryTypeChange,
  deliveryOption: requestedDeliveryOption = "custom",
  onDeliveryOptionChange,
  selectedLocationId = "",
  onLocationSelect,
  customAddress,
  onCustomAddressChange,
  deliveryLocations,
  deliveryFee,
  deliveryDistance,
  isCalculatingDistance = false,
  deliveryAvailable = true,
  customAddressAvailable = true,
  onAddressSelected,
  googleMapsApiKey,
  className = "",
}: DeliveryOptionsSelectorProps) {
  const [hasLocations, setHasLocations] = useState(false);
  // Without typed addresses the only possible choice is a location.
  const deliveryOption = customAddressAvailable
    ? requestedDeliveryOption
    : "location";

  useEffect(() => {
    setHasLocations(deliveryLocations && deliveryLocations.length > 0);
  }, [deliveryLocations]);

  const formatDeliveryFee = (fee: number) => {
    return `$${fee.toFixed(2)}`;
  };

  const formatDistance = (distance: number | null) => {
    if (distance === null || distance === undefined) return "Calculating...";
    return `${distance.toFixed(2)} miles`;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Delivery Type Selection */}
      <div>
        <Label className="text-sm font-medium text-gray-700 mb-2 block">
          Delivery Type
        </Label>
        <RadioGroup
          value={deliveryType}
          onValueChange={(value) =>
            onDeliveryTypeChange(value as "pickup" | "delivery")
          }
        >
          <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 transition-colors">
            <RadioGroupItem value="pickup" id="pickup" />
            <Label htmlFor="pickup" className="cursor-pointer flex-1">
              <div className="font-medium">Pick up at vehicle location</div>
              <div className="text-sm text-gray-500">
                You'll pick up the vehicle at the designated location
              </div>
            </Label>
          </div>

          <div
            className={`flex items-center space-x-2 p-3 border rounded-lg transition-colors ${
              deliveryAvailable
                ? "hover:bg-gray-50"
                : "opacity-50 cursor-not-allowed"
            }`}
          >
            <RadioGroupItem
              value="delivery"
              id="delivery"
              disabled={!deliveryAvailable}
            />
            <Label
              htmlFor="delivery"
              className={`flex-1 ${!deliveryAvailable ? "cursor-not-allowed" : "cursor-pointer"}`}
            >
              <div className="font-medium">
                Delivery to your location
                {!deliveryAvailable && (
                  <span className="ml-2 text-xs text-red-600">
                    (Not available)
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-500">
                {customAddressAvailable
                  ? "We'll deliver the vehicle to your specified address"
                  : "We'll deliver the vehicle to one of the listed delivery locations"}
              </div>
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Delivery Options (only show if delivery is selected) */}
      {deliveryType === "delivery" && deliveryAvailable && (
        <div className="space-y-4 pl-4 border-l-2 border-blue-200">
          {/* Delivery Method Selection */}
          {customAddressAvailable && hasLocations && (
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Delivery Method
              </Label>
              <RadioGroup
                value={deliveryOption}
                onValueChange={(value) =>
                  onDeliveryOptionChange(value as "custom" | "location")
                }
              >
                <div className="flex items-center space-x-2 p-2">
                  <RadioGroupItem value="custom" id="custom" />
                  <Label htmlFor="custom" className="cursor-pointer">
                    Enter custom address
                  </Label>
                </div>

                <div className="flex items-center space-x-2 p-2">
                  <RadioGroupItem value="location" id="location" />
                  <Label htmlFor="location" className="cursor-pointer">
                    Choose from predefined locations
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {/* Custom Address Input */}
          {deliveryOption === "custom" && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Delivery Address
              </Label>

              {/* Google Places Autocomplete */}
              <GooglePlacesAutocomplete
                value={customAddress?.address || ""}
                onChange={(value) => {
                  // Update address string while typing
                  if (!value) {
                    onCustomAddressChange(null);
                    onAddressSelected?.(false);
                  } else {
                    // Keep existing coordinates if any, just update the address text
                    onCustomAddressChange({
                      address: value,
                      city: customAddress?.city || "",
                      state: customAddress?.state || "",
                      zipCode: customAddress?.zipCode || "",
                      country: customAddress?.country || "US",
                      lat: customAddress?.lat || 0,
                      lng: customAddress?.lng || 0,
                    });
                    // Mark as not selected yet (user is still typing)
                    onAddressSelected?.(false);
                  }
                }}
                onPlaceSelect={(place) => {
                  console.log(
                    "🔵 GooglePlacesAutocomplete - Place selected:",
                    place,
                  );
                  // Update full address object with coordinates when place is selected
                  const newAddress = {
                    address: place.address,
                    city: place.city,
                    state: place.state,
                    zipCode: place.zipCode || "",
                    country: place.country,
                    lat: place.lat,
                    lng: place.lng,
                  };
                  console.log("🔵 Updating customAddress:", newAddress);
                  onCustomAddressChange(newAddress);
                  console.log("🔵 Calling onAddressSelected(true)");
                  onAddressSelected?.(true);
                }}
                placeholder="Enter delivery address"
                apiKey={googleMapsApiKey || ""}
              />

              {/* Show selected address info with map */}
              {customAddress && (
                <div className="space-y-2">
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">
                        {customAddress.address}
                      </div>
                      <div className="text-gray-600">
                        {customAddress.city}, {customAddress.state}{" "}
                        {customAddress.zipCode}
                      </div>
                      {customAddress.country && (
                        <div className="text-gray-600">
                          {customAddress.country}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Interactive Map for visual confirmation */}
                  <GoogleMap
                    center={{ lat: customAddress.lat, lng: customAddress.lng }}
                    height="200px"
                    apiKey={googleMapsApiKey || ""}
                    onLocationSelect={(location) => {
                      console.log(
                        "🟢 GoogleMap - Location selected (drag/click):",
                        location,
                      );
                      // Update address when marker is dragged or map is clicked
                      const newAddress = {
                        address: location.address,
                        city: location.city || "",
                        state: location.state || "",
                        zipCode: location.zipCode || "",
                        country: location.country || "US",
                        lat: location.lat,
                        lng: location.lng,
                      };
                      console.log("🟢 Updating customAddress:", newAddress);
                      onCustomAddressChange(newAddress);
                      console.log("🟢 Calling onAddressSelected(true)");
                      // Mark as selected to trigger distance calculation
                      onAddressSelected?.(true);
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Predefined Location Select */}
          {deliveryOption === "location" && hasLocations && (
            <div className="space-y-2">
              <Label
                htmlFor="location-select"
                className="text-sm font-medium text-gray-700"
              >
                Select Delivery Location
              </Label>
              <Select
                value={selectedLocationId}
                onValueChange={onLocationSelect}
              >
                <SelectTrigger id="location-select">
                  <SelectValue placeholder="Choose a location" />
                </SelectTrigger>
                <SelectContent>
                  {deliveryLocations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      <div className="flex items-center justify-between w-full">
                        <span>{location.location_name}</span>
                        <span className="ml-4 text-gray-500">
                          ${Number(location.delivery_fee).toFixed(2)}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedLocationId && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  {(() => {
                    const selectedLocation = deliveryLocations.find(
                      (loc) => loc.id === selectedLocationId,
                    );
                    return selectedLocation ? (
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">
                          {selectedLocation.location_name}
                        </div>
                        {selectedLocation.address && (
                          <div className="text-gray-600">
                            {selectedLocation.address}
                          </div>
                        )}
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Delivery Fee Summary */}
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-900">Delivery Fee</div>
                {deliveryOption === "custom" && deliveryDistance !== null && (
                  <div className="text-sm text-gray-600 mt-1">
                    Distance: {formatDistance(deliveryDistance)}
                    {isCalculatingDistance && (
                      <span className="ml-2 text-blue-600">Calculating...</span>
                    )}
                  </div>
                )}
              </div>
              <div className="text-xl font-bold text-gray-900">
                {isCalculatingDistance ? (
                  <span className="text-base text-gray-500">
                    Calculating...
                  </span>
                ) : (
                  formatDeliveryFee(deliveryFee)
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
